import { Rules, ScopePostCode } from '@/constants/enum';
import { IParamsApi } from '@/types/apis/params';
import { IResponseApi } from '@/types/apis/response';
import { IEstimateRules } from '@/types/components/settings';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { InitDataSettings, InitShopInfo, InitZipcodeSettings } from './initialState';
import { checkIfIsLightColor } from '@/helpers';

export interface IDataSettingState extends IParamsApi.IUpdateGeneralSetting {
  id: number;
}
export interface ISettingsState {
  activeKey: string;
  rule: Rules;
  shopInfo: IResponseApi.IShopInfo['data'];
  estimateRules: IEstimateRules;
  dataSettings: IDataSettingState;
  dataSettingsBackup: IDataSettingState;
  checkDataSetting: boolean;
  checkDeliveryMethodSetting: boolean;
  checkZipcodeSetting: boolean;
  locationInfo?: IResponseApi.ICheckIpLocation;
  modeLight?: boolean;
}

// Define the initial state using that type
const initialState: ISettingsState = {
  activeKey: 'generalSettings',
  rule: Rules.Methods,
  shopInfo: InitShopInfo,
  dataSettings: InitDataSettings,
  dataSettingsBackup: InitDataSettings,
  estimateRules: {
    method: {
      moreInfo: '',
      ourPolicy: '',
      policyUrl: '',
    },
    postCode: {
      scope: ScopePostCode.AllProduct,
    },
  },
  checkDataSetting: false,
  checkDeliveryMethodSetting: false,
  checkZipcodeSetting: false,
  locationInfo: undefined,
  modeLight: true,
};

const settingsSlice = createSlice({
  name: 'settings',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    handleActiveKey: (state, action: PayloadAction<string>) => {
      state.activeKey = action.payload;
    },
    handleRule: (state, action: PayloadAction<Rules>) => {
      state.rule = action.payload;
      if (state.dataSettings) {
        state.dataSettings.layout = action.payload;
      }
    },
    handleDataSettings: (state, action: PayloadAction<IDataSettingState>) => {
      const newDataSettings: IDataSettingState = {
        ...action.payload,
        zipcodeSetting: action.payload.zipcodeSetting ? action.payload.zipcodeSetting : InitZipcodeSettings,
        durationInSeconds: action.payload.durationInSeconds || InitDataSettings.durationInSeconds,
        sessionBehavior: action.payload.sessionBehavior || InitDataSettings.sessionBehavior,
        endBehavior: action.payload.endBehavior || InitDataSettings.endBehavior,
      };
      if (action.payload) {
        state.rule = action.payload.layout;
        state.dataSettings = newDataSettings;
        if (action.payload?.text_color) {
          const currentViewMode = checkIfIsLightColor(action.payload?.text_color);
          state.modeLight = !currentViewMode;
        }
      }
    },
    handleDataSettingsBackup: (state, action: PayloadAction<IDataSettingState>) => {
      const newDataSettings = {
        ...action.payload,
        zipcodeSetting: action.payload.zipcodeSetting ? action.payload.zipcodeSetting : InitZipcodeSettings,
        durationInSeconds: action.payload.durationInSeconds || InitDataSettings.durationInSeconds,
        sessionBehavior: action.payload.sessionBehavior || InitDataSettings.sessionBehavior,
        endBehavior: action.payload.endBehavior || InitDataSettings.endBehavior,
      };
      state.dataSettingsBackup = newDataSettings;
    },
    handleShopInfo: (state, action: PayloadAction<IResponseApi.IShopInfo['data']>) => {
      state.shopInfo = action.payload;
    },
    handleLocationInfo: (state, action: PayloadAction<IResponseApi.ICheckIpLocation>) => {
      state.locationInfo = action.payload;
    },
    handleModeLight: (state) => {
      state.modeLight = !state.modeLight;
    },
  },
});

// Other code such as selectors can use the imported `RootState` type
export const activeKeySelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.activeKey,
);

export const ruleSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.rule,
);

export const dataSettingsSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.dataSettings,
);

export const dataSettingsBackupSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.dataSettingsBackup,
);

export const shopInfoSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.shopInfo,
);

export const locationInfoSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.locationInfo,
);

export const modeLightSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.modeLight,
);

export default settingsSlice;
